/**
 * Event Details Dialog Component
 * Main dialog for viewing and managing event details
 */
import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Calendar, Music, ClipboardList, CheckSquare, Percent, FileText, 
  DollarSign, CreditCard, File, Plus, MapPin, Mail, MessageSquare, 
  Clock, X, LayoutGrid, ExternalLink, Lock, Unlock
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Import tab components
import { TimelineTab } from './TimelineTab';
import { TasksTab } from './TasksTab';
import { DocumentsTab } from './DocumentsTab';
import { MessagesTab } from './MessagesTab';
import { ActivityNotesTab } from './ActivityNotesTab';
import { ContractsTab } from './ContractsTab';
import { PaymentsTab } from './PaymentsTab';
import SeatingChartEditor from '@/components/SeatingChartEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function EventDetailsDialog({
  open,
  onOpenChange,
  booking,
  onBookingUpdate,
  initialTab = 'timeline',
  formatDate,
  formatTime,
  formatCurrency,
  userName,
  onOpenCounterSign,
  // Data passed from parent
  playlists = [],
  timeline = null,
  forms = [],
  formResponses = [],
  tasks = [],
  documents = [],
  contracts = [],
  invoices = [],
  expenses = [],
  messages = [],
  activityLog = [],
  addonRequests = [],
  venueChangeRequests = [],
  missingSongs = {},
  // Refresh callbacks
  onPlaylistsRefresh,
  onTimelineRefresh,
  onFormsRefresh,
  onTasksRefresh,
  onDocumentsRefresh,
  onContractsRefresh,
  onInvoicesRefresh,
  onExpensesRefresh,
  onMessagesRefresh,
  onActivityLogRefresh,
  onAddonRequestsRefresh,
  onVenueChangeRequestsRefresh,
  // Additional handlers
  onOpenPlaylistEditor,
  onOpenFormDialog,
  onOpenExpenseDialog,
  onOpenInvoiceDialog,
  // Settings
  settings,
  availableDiscounts = []
}) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [lockoutOverrides, setLockoutOverrides] = useState({
    forms_locked_override: null,
    timeline_locked_override: null,
    playlists_locked_override: null
  });

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (booking) {
      setLockoutOverrides({
        forms_locked_override: booking.forms_locked_override ?? null,
        timeline_locked_override: booking.timeline_locked_override ?? null,
        playlists_locked_override: booking.playlists_locked_override ?? null
      });
    }
  }, [booking]);

  const toggleLockout = async (field) => {
    const current = lockoutOverrides[field];
    // Cycle: null (default) -> true (locked) -> false (unlocked) -> null
    const next = current === null ? true : current === true ? false : null;
    const newOverrides = { ...lockoutOverrides, [field]: next };
    setLockoutOverrides(newOverrides);
    try {
      await axios.put(`${API}/bookings/${booking.id}/lockout-override`, { [field]: next }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const label = field.replace('_locked_override', '').replace('_', ' ');
      const stateLabel = next === true ? 'locked' : next === false ? 'unlocked' : 'using default';
      toast.success(`${label.charAt(0).toUpperCase() + label.slice(1)} ${stateLabel}`);
      if (onBookingUpdate) onBookingUpdate();
    } catch (err) {
      toast.error('Failed to update lockout');
      setLockoutOverrides(prev => ({ ...prev, [field]: current }));
    }
  };

  const navItems = [
    { id: 'timeline', label: 'Timeline', icon: Calendar },
    { id: 'playlists', label: 'Playlists', icon: Music },
    { id: 'seating', label: 'Seating Chart', icon: LayoutGrid },
    { id: 'forms', label: 'Forms', icon: ClipboardList },
    { id: 'tasks', label: 'Tasks', icon: CheckSquare, badge: tasks.filter(t => t.status !== 'completed').length, badgeColor: tasks.some(t => t.is_overdue) ? 'bg-red-500' : 'bg-orange-500' },
    { id: 'discounts', label: 'Discounts', icon: Percent, badge: booking?.applied_discounts?.length || 0, badgeColor: 'bg-neon-purple' },
    { id: 'invoices', label: 'Invoices', icon: FileText, badge: invoices.length, badgeColor: 'bg-neon-blue' },
    { id: 'contracts', label: 'Contracts', icon: FileText, badge: contracts.filter(c => c.status === 'client_signed').length, badgeColor: 'bg-orange-500' },
    { id: 'payments', label: 'Payments', icon: DollarSign },
    { id: 'expenses', label: 'Expenses', icon: CreditCard, badge: expenses.length, badgeColor: 'bg-purple-500' },
    { id: 'documents', label: 'Documents', icon: File, badge: documents.length, badgeColor: 'bg-muted' },
    { id: 'addon-requests', label: 'Add-on Requests', icon: Plus, badge: addonRequests.length, badgeColor: 'bg-neon-purple' },
    { id: 'venue-changes', label: 'Venue Changes', icon: MapPin, badge: venueChangeRequests.length, badgeColor: 'bg-emerald-500' },
    { id: 'communications', label: 'Communications', icon: Mail },
    { id: 'messages', label: 'Messages', icon: MessageSquare, badge: messages.filter(m => m.sender_type === 'client' && !m.is_read).length, badgeColor: 'bg-red-500' },
    { id: 'notes', label: 'Activity Notes', icon: Clock, badge: activityLog.length, badgeColor: 'bg-yellow-500' },
  ];

  const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/bookings/${booking.id}`, { status: newStatus }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      onBookingUpdate();
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border border-border max-w-5xl max-h-[85vh] p-0 overflow-hidden" data-testid="event-details-dialog">
        <div className="flex h-[85vh]">
          {/* Left Sidebar Navigation */}
          <div className="w-64 bg-muted/30 border-r border-border flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-border">
              <h2 className="font-bold text-lg truncate">{booking.client_name}</h2>
              <p className="text-sm text-muted-foreground">{booking.event_type}</p>
              <p className="text-sm text-muted-foreground">{formatDate ? formatDate(booking.event_date) : booking.event_date}</p>
              
              {/* Status Selector */}
              <div className="mt-3">
                <Select 
                  value={booking.status} 
                  onValueChange={handleStatusChange}
                  disabled={updatingStatus}
                >
                  <SelectTrigger className="bg-muted border-border h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inquiry">Inquiry</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Client Portal Link */}
              {booking.client_portal_token && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full mt-2 border-border text-xs"
                  onClick={() => window.open(`/portal/${booking.client_portal_token}`, '_blank')}
                >
                  <ExternalLink className="w-3 h-3 mr-1" />
                  Client Portal
                </Button>
              )}
            </div>

            {/* Lockout Overrides */}
            <div className="px-3 py-2 border-b border-border">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 font-semibold">Portal Lockout</p>
              <div className="space-y-1">
                {[
                  { field: 'forms_locked_override', label: 'Forms', icon: ClipboardList },
                  { field: 'timeline_locked_override', label: 'Timeline', icon: Calendar },
                  { field: 'playlists_locked_override', label: 'Playlists', icon: Music }
                ].map(({ field, label, icon: Icon }) => {
                  const val = lockoutOverrides[field];
                  const isLocked = val === true;
                  const isUnlocked = val === false;
                  return (
                    <button
                      key={field}
                      onClick={() => toggleLockout(field)}
                      data-testid={`lockout-toggle-${field}`}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${
                        isLocked ? 'bg-red-500/15 text-red-400' : 
                        isUnlocked ? 'bg-emerald-500/15 text-emerald-400' : 
                        'bg-muted/50 text-muted-foreground'
                      } hover:bg-muted`}
                      title={isLocked ? `${label}: Locked (click to unlock)` : isUnlocked ? `${label}: Unlocked (click for default)` : `${label}: Using default (click to lock)`}
                    >
                      {isLocked ? <Lock className="w-3 h-3" /> : isUnlocked ? <Unlock className="w-3 h-3" /> : <Icon className="w-3 h-3 opacity-50" />}
                      <span className="flex-1 text-left">{label}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                        isLocked ? 'bg-red-500/20 text-red-300' : 
                        isUnlocked ? 'bg-emerald-500/20 text-emerald-300' : 
                        'bg-muted text-muted-foreground'
                      }`}>
                        {isLocked ? 'Locked' : isUnlocked ? 'Open' : 'Default'}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Navigation Items */}
            <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    data-testid={`event-nav-${item.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive 
                        ? 'bg-neon-blue/20 text-neon-blue' 
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.badge > 0 && (
                      <span className={`px-1.5 py-0.5 rounded-full text-xs ${item.badgeColor} text-white`}>
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Close Button */}
            <div className="p-2 border-t border-border">
              <Button 
                variant="outline" 
                className="w-full border-border"
                onClick={() => onOpenChange(false)}
              >
                <X className="w-4 h-4 mr-2" />
                Close
              </Button>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Timeline Tab */}
            {activeTab === 'timeline' && (
              <TimelineTab
                booking={booking}
                timeline={timeline}
                onTimelineUpdate={onTimelineRefresh}
                formatTime={formatTime}
              />
            )}

            {/* Seating Chart Tab */}
            {activeTab === 'seating' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <LayoutGrid className="w-5 h-5 text-purple-400" />
                  Seating Chart
                </h3>
                <div className="bg-gray-900/50 rounded-xl border border-gray-700 overflow-hidden" style={{ height: '550px' }}>
                  <SeatingChartEditor 
                    bookingId={booking.id}
                    isClientPortal={false}
                  />
                </div>
              </div>
            )}

            {/* Tasks Tab */}
            {activeTab === 'tasks' && (
              <TasksTab
                booking={booking}
                tasks={tasks}
                onTasksUpdate={onTasksRefresh}
                formatDate={formatDate}
              />
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <DocumentsTab
                booking={booking}
                documents={documents}
                onDocumentsUpdate={onDocumentsRefresh}
                formatDate={formatDate}
              />
            )}

            {/* Messages Tab */}
            {activeTab === 'messages' && (
              <MessagesTab
                booking={booking}
                messages={messages}
                onMessagesUpdate={onMessagesRefresh}
                formatDate={formatDate}
                userName={userName}
              />
            )}

            {/* Activity Notes Tab */}
            {activeTab === 'notes' && (
              <ActivityNotesTab
                booking={booking}
                notes={activityLog}
                onNotesUpdate={onActivityLogRefresh}
                formatDate={formatDate}
                userName={userName}
              />
            )}

            {/* Contracts Tab */}
            {activeTab === 'contracts' && (
              <ContractsTab
                booking={booking}
                contracts={contracts}
                onContractsUpdate={onContractsRefresh}
                onOpenCounterSign={onOpenCounterSign}
                formatDate={formatDate}
              />
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && (
              <PaymentsTab
                booking={booking}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
              />
            )}

            {/* Placeholder for tabs that need more complex logic */}
            {['playlists', 'forms', 'discounts', 'invoices', 'expenses', 'addon-requests', 'venue-changes', 'communications'].includes(activeTab) && (
              <div className="text-center py-12 text-muted-foreground">
                <p>Tab content loaded from parent component</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
